import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ItemsList } from '@/components/ItemsList';
import { ChangeHistory } from '@/components/ChangeHistory';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorMessage } from '@/components/ErrorMessage';
import { api, APIError } from '@/api/client';
import type { SOW } from '@/types';

export function OwnerSOWView() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [sow, setSOW] = useState<SOW | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');

  const token = location.state?.token || sow?.clientAccessToken;

  useEffect(() => {
    if (!id) return;

    api
      .getSOW(id)
      .then((response) => {
        setSOW(response.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof APIError) {
          setError(err.message);
        } else {
          setError('Failed to load SOW');
        }
        setLoading(false);
      });
  }, [id]);

  const copyToken = async () => {
    if (token) {
      setCopyError('');
      try {
        const url = `${window.location.origin}/client/${token}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        setCopyError('Failed to copy to clipboard. Please copy manually.');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorMessage message={error} />
      </Layout>
    );
  }

  if (!sow) {
    return (
      <Layout>
        <ErrorMessage message="SOW not found" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8">
        {copyError && <ErrorMessage message={copyError} />}

        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Statement of Work
          </h1>
          <p className="text-gray-600 mt-2">
            Owner: {sow.owner} | Created:{' '}
            {new Date(sow.createdAt).toLocaleDateString()}
          </p>
        </div>

        {token && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Client Access Token
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/client/${token}`}
                className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded text-sm"
              />
              <button
                onClick={copyToken}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Current Items
          </h2>
          <ItemsList items={sow.items} />
        </div>

        {sow.changes && sow.changes.length > 0 && (
          <ChangeHistory changes={sow.changes} />
        )}
      </div>
    </Layout>
  );
}
